<template>
  <div v-if="showLive && entries.length > 0" class="relative">
    <button
      type="button"
      class="score-pill"
      @click="isOpen = true"
    >
      <TransitionGroup name="score-chip" tag="div" class="score-pill-chips">
        <div
          v-for="entry in topTenEntries"
          :key="entry.userId"
          class="score-chip"
        >
          <span class="score-chip-initials">{{ toInitials(entry.displayName) }}</span>
          <span class="score-chip-value">{{ formatCompactScore(entry.score) }}</span>
        </div>
      </TransitionGroup>
    </button>

    <Teleport to="body">
      <div v-if="isOpen" class="leaderboard-modal" @click.self="isOpen = false">
        <div class="leaderboard-panel">
          <div class="leaderboard-header">
            <h3 class="leaderboard-title">Live Leaderboard</h3>
            <button class="leaderboard-close" @click="isOpen = false">Close</button>
          </div>

          <div class="leaderboard-list">
            <div
              v-for="(entry, index) in sortedEntries"
              :key="entry.userId"
              class="leaderboard-row"
            >
              <span
                :class="[
                  'leaderboard-rank',
                  index === 0 ? 'leaderboard-rank-1' :
                  index === 1 ? 'leaderboard-rank-2' :
                  index === 2 ? 'leaderboard-rank-3' :
                  'leaderboard-rank-default'
                ]"
              >
                {{ index + 1 }}
              </span>
              <span class="leaderboard-name">{{ entry.displayName }}</span>
              <span class="leaderboard-score">{{ entry.score.toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

type ScoreboardEntry = {
  userId: string;
  displayName: string;
  score: number;
};

const props = defineProps<{
  entries: ScoreboardEntry[];
  showLive?: boolean;
}>();

const isOpen = ref(false);

const sortedEntries = computed(() => {
  return [...props.entries].sort((a, b) => b.score - a.score || a.displayName.localeCompare(b.displayName));
});

const topTenEntries = computed(() => sortedEntries.value.slice(0, 10));

const toInitials = (displayName: string) => {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
};

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatCompactScore = (value: number) => compactFormatter.format(value);
</script>

<style scoped>
.score-pill {
  display: inline-flex;
  align-items: stretch;
  max-width: min(88vw, 460px);
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: color-mix(in srgb, var(--color-surface) 90%, transparent);
  backdrop-filter: blur(8px);
  color: var(--color-text-primary);
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.score-pill::-webkit-scrollbar {
  display: none;
}

.score-pill-chips {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.score-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  padding: 4px 7px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.score-chip-initials {
  font-size: 10px;
  font-weight: 800;
  color: var(--color-text-primary);
  letter-spacing: 0.01em;
}

.score-chip-value {
  font-size: 10px;
  font-weight: 700;
  color: var(--color-text-secondary);
}

.score-chip-move,
.score-chip-enter-active,
.score-chip-leave-active {
  transition: all 0.28s ease;
}

.score-chip-enter-from,
.score-chip-leave-to {
  opacity: 0;
  transform: translateY(-3px) scale(0.96);
}

.score-chip-leave-active {
  position: absolute;
}

.leaderboard-modal {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 8, 14, 0.62);
  padding: 16px;
}

.leaderboard-panel {
  width: min(900px, 96vw);
  max-height: min(78vh, 680px);
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: color-mix(in srgb, var(--color-surface) 94%, black 6%);
}

.leaderboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.leaderboard-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.leaderboard-close {
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  background: transparent;
  color: var(--color-text-primary);
  padding: 6px 10px;
  font-size: 12px;
}

.leaderboard-list {
  overflow: auto;
  padding: 10px 12px 12px;
}

.leaderboard-row {
  display: grid;
  grid-template-columns: 32px 1fr auto;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 8px;
}

.leaderboard-rank {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}

.leaderboard-rank-1 { background: #eab308; color: #111827; }
.leaderboard-rank-2 { background: #9ca3af; color: #111827; }
.leaderboard-rank-3 { background: #b45309; color: #f9fafb; }
.leaderboard-rank-default { background: rgba(255, 255, 255, 0.12); color: var(--color-text-secondary); }

.leaderboard-name {
  min-width: 0;
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.leaderboard-score {
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 700;
}
</style>
